<script setup lang="ts">
import './assets/object-type-card.scss'
import { computed, ref, toRef, watch, type PropType } from 'vue'
import { debounce } from 'lodash'
import type { ObjectTypeTypeShort } from '@/entities/Manuals/ObjectTypes'
import { VIcons } from '@/shared/ui'
import { useObjectTypeGroupElements } from '../model/useObjectTypeGroupElements'
import ObjectTypeGroupRowActions from './ObjectTypeGroupRowActions.vue'

const props = defineProps({
  type: {
    type: Object as PropType<ObjectTypeTypeShort>,
    required: true,
  },
  objectId: {
    type: String,
    required: true,
  },
  section: {
    type: String,
    required: true,
  },
})
const emits = defineEmits(['changed'])
const modelValue = defineModel<boolean>({ required: true })

const searchDraft = ref('')
const typeId = computed(() => Number(props.type.id))
const { page, perPage, items, total, isPending, pageCount, applySearch, setPerPage, refetch } =
  useObjectTypeGroupElements(
    toRef(props, 'objectId'),
    typeId,
    toRef(props, 'section'),
    modelValue,
  )

/* Диапазон видимых элементов для подвала пагинации */
const rangeFrom = computed(() => (total.value ? (page.value - 1) * perPage.value + 1 : 0))
const rangeTo = computed(() => Math.min(page.value * perPage.value, total.value))

/* Дебаунс применения поиска при вводе */
const debouncedApply = debounce(() => applySearch(searchDraft.value.trim()), 400)

/**
 * Функция применения поиска по Enter — без ожидания дебаунса
 */
const searchSubmitHandler = () => {
  debouncedApply.cancel()
  applySearch(searchDraft.value.trim())
}

/**
 * Функция очистки поиска
 */
const searchClearHandler = () => {
  searchDraft.value = ''
  searchSubmitHandler()
}

/**
 * Функция обновления данных после удаления/дублирования строки:
 * перезагружаем дочерние элементы и оповещаем страницу
 */
const changedHandler = () => {
  refetch()
  emits('changed')
}

/* Сбрасываем поиск при закрытии оверлея */
watch(modelValue, (opened) => {
  if (!opened) searchClearHandler()
})
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="800"
    @update:model-value="modelValue = $event"
  >
    <VCard class="object-type-overlay" rounded="xl">
      <VCardTitle class="object-type-overlay__header">
        <span class="object-type-card__parent-name">{{ type.parent_name }}</span>
        <VSpacer />
        <VBtn icon variant="text" density="compact" @click="modelValue = false">
          <VIcons type="close" />
        </VBtn>
      </VCardTitle>

      <VCardText class="object-type-overlay__body">
        <VTextField
          v-model="searchDraft"
          :label="$t('itf.search')"
          density="compact"
          hide-details
          clearable
          class="object-type-overlay__search"
          @update:model-value="debouncedApply()"
          @keydown.enter="searchSubmitHandler"
          @click:clear="searchClearHandler"
        />

        <VProgressLinear v-if="isPending" indeterminate color="primary" />

        <div class="object-type-overlay__list">
          <template v-for="item in items" :key="item.id">
            <div class="object-type-card__row object-type-overlay__row">
              <span class="object-type-card__id">{{ item.id }}</span>
              <span class="object-type-card__name">{{ item.name }}</span>

              <ObjectTypeGroupRowActions
                :element-id="item.id"
                :type-id="typeId"
                :object-id="objectId"
                :section="section"
                @changed="changedHandler"
              />
            </div>
            <VDivider />
          </template>

          <div v-if="!isPending && !items.length" class="object-type-card__empty">
            {{ $t('itf.empty_search') }}
          </div>
        </div>

        <div class="object-type-overlay__pagination">
          <VSelect
            class="object-type-overlay__per-page"
            :items="[10, 50, 100]"
            :model-value="perPage"
            density="compact"
            variant="outlined"
            hide-details
            @update:model-value="setPerPage"
          />

          <VPagination
            v-if="pageCount > 1"
            v-model="page"
            :length="pageCount"
            :total-visible="7"
            density="compact"
            prev-icon="chevron_left"
            next-icon="chevron_right"
          />

          <span class="object-type-overlay__range">
            {{ rangeFrom }}–{{ rangeTo }} {{ $t('itf.pagination_of') }} {{ total }}
          </span>
        </div>
      </VCardText>
    </VCard>
  </VDialog>
</template>
